import type { DirectoryOrganization, PagedPayload } from "@noverik/contracts";
import { Building2, ChevronLeft, ChevronRight, Plus, Search } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";

export function OrganizationsPage() {
  const { session } = useAuth();
  const [data, setData] = useState<PagedPayload<DirectoryOrganization> | null>(null);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({ page: String(page), limit: "25" });
    if (query) params.set("q", query);

    try {
      const next = await apiRequest<PagedPayload<DirectoryOrganization>>(
        "/organizations?" + params.toString(),
      );
      setData(next);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Não foi possível carregar as organizações.");
    } finally {
      setLoading(false);
    }
  }, [page, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setPage(1);
    setQuery(input.trim());
  };

  const createOrganization = async (event: FormEvent) => {
    event.preventDefault();
    if (!session?.csrfToken) return;

    setSaving(true);
    setError(null);

    try {
      await apiRequest<DirectoryOrganization>("/organizations", {
        method: "POST",
        csrfToken: session.csrfToken,
        body: { name },
      });
      setName("");
      setShowCreate(false);
      setPage(1);
      await load();
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : "Não foi possível cadastrar a organização.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">RELACIONAMENTO</span>
          <h1>Organizações</h1>
          <p>Agrupe clientes por empresa sem compartilhar chamados entre os membros.</p>
        </div>
        <button className="button primary" onClick={() => setShowCreate((value) => !value)}>
          <Plus size={17} /> Nova organização
        </button>
      </div>

      {showCreate && (
        <form className="inline-create-card" onSubmit={createOrganization}>
          <div className="inline-create-heading">
            <span className="meta-icon purple"><Building2 size={18} /></span>
            <div>
              <strong>Cadastrar organização</strong>
              <span>Novas organizações são criadas com visibilidade privada entre usuários.</span>
            </div>
          </div>
          <div className="inline-create-grid org">
            <label className="field">
              <span>Nome da organização</span>
              <input value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
            <button className="button primary" disabled={saving}>
              {saving ? "Salvando..." : "Cadastrar"}
            </button>
          </div>
        </form>
      )}

      <div className="ticket-controls">
        <form className="searchbox inline" onSubmit={submitSearch}>
          <Search size={17} />
          <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="Buscar organização..." />
          <button type="submit" className="search-submit">Buscar</button>
        </form>
      </div>

      {error && <div className="data-message error">{error}</div>}

      {!error && (
        <div className="organization-grid">
          {loading && <div className="data-message">Carregando organizações...</div>}
          {!loading && data?.items.length === 0 && <div className="data-message">Nenhuma organização encontrada.</div>}
          {!loading && data?.items.map((org) => (
            <article className="organization-card" key={org.id}>
              <span className="meta-icon purple"><Building2 size={20} /></span>
              <div>
                <strong>{org.name}</strong>
                <span>{org.userCount} cliente{org.userCount === 1 ? "" : "s"}</span>
                <small>Visibilidade individual de chamados</small>
              </div>
            </article>
          ))}
        </div>
      )}

      {!loading && data && data.pages > 1 && (
        <div className="pagination">
          <span>Página {data.page} de {data.pages}</span>
          <div>
            <button className="button secondary" disabled={data.page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
              <ChevronLeft size={16} /> Anterior
            </button>
            <button className="button secondary" disabled={data.page >= data.pages} onClick={() => setPage((value) => value + 1)}>
              Próxima <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
